import { useRef, useEffect, useState, useMemo } from 'react';

/**
 * Constants for marker smoothing behavior
 */
const TELEPORT_THRESHOLD_KM = 0.5; // 500m - snap immediately if jump is larger
const STALE_THRESHOLD_MS = 60000; // 60 seconds - snap if update is old
const INTERPOLATION_DURATION_MS = 1000; // Smooth over 1 second
const IDLE_SPEED_THRESHOLD = 0.5; // knots - don't animate if speed below this
const ANIMATION_INTERVAL_MS = 16; // ~60fps

/**
 * Calculate distance between two points in km using Haversine formula
 */
const haversineDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // Earth radius in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

/**
 * Linear interpolation between two values
 */
const lerp = (start, end, t) => start + (end - start) * Math.min(1, Math.max(0, t));

/**
 * Ease-out cubic function for smoother deceleration
 */
const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

/**
 * Determines if we should snap to position instead of animating
 */
const shouldSnapToPosition = (prev, current) => {
  const { lat, lon, speed = 0, motion_state, updated_at } = current;
  
  // First position - snap
  if (!prev) return true;
  
  // Anchored or idle - no jitter animation
  if (motion_state === 'ANCHORED' || motion_state === 'IDLE' || speed < IDLE_SPEED_THRESHOLD) {
    return true;
  }
  
  // Large distance jump (teleport)
  const distance = haversineDistance(prev.lat, prev.lon, lat, lon);
  if (distance > TELEPORT_THRESHOLD_KM) {
    return true;
  }
  
  // Stale update
  if (updated_at) {
    const updateAge = Date.now() - new Date(updated_at).getTime();
    if (updateAge > STALE_THRESHOLD_MS) {
      return true;
    }
  }
  
  return false;
};

/**
 * Hook for managing multiple smoothed markers (friends)
 * Uses setInterval for smooth interpolation to avoid React hook complexity
 * @param {Array} friends - Array of friend location objects
 * @returns {Object} - Map of friend_user_id -> smoothed position
 */
export const useMultipleMarkerSmoothing = (friends) => {
  // Memoize the current positions from friends
  const currentPositions = useMemo(() => {
    if (!friends || !Array.isArray(friends)) return {};
    const positions = {};
    friends.forEach(f => {
      if (f.friend_user_id && f.lat != null && f.lon != null) {
        positions[f.friend_user_id] = {
          lat: f.lat,
          lon: f.lon,
          speed: f.speed || 0,
          motion_state: f.motion_state,
          updated_at: f.updated_at,
        };
      }
    });
    return positions;
  }, [friends]);

  const [smoothedPositions, setSmoothedPositions] = useState({});
  const stateRef = useRef({
    previous: {},
    targets: {},
    lastProcessed: {},
  });

  // Process incoming friend data and set up animations
  useEffect(() => {
    const state = stateRef.current;
    const friendIds = Object.keys(currentPositions);
    
    friendIds.forEach(id => {
      const current = currentPositions[id];
      const prev = state.previous[id];
      const lastProcessed = state.lastProcessed[id];
      
      // Skip if we already processed this exact update
      if (lastProcessed && 
          lastProcessed.lat === current.lat && 
          lastProcessed.lon === current.lon) {
        return;
      }
      
      state.lastProcessed[id] = { lat: current.lat, lon: current.lon };
      
      if (shouldSnapToPosition(prev, current)) {
        // Snap immediately
        state.previous[id] = { lat: current.lat, lon: current.lon };
        delete state.targets[id];
      } else {
        // Set up animation
        state.targets[id] = {
          lat: current.lat,
          lon: current.lon,
          startTime: performance.now(),
          startLat: prev.lat,
          startLon: prev.lon,
        };
      }
    });
    
    // Cleanup removed friends
    Object.keys(state.previous).forEach(id => {
      if (!currentPositions[id]) {
        delete state.previous[id];
        delete state.targets[id];
        delete state.lastProcessed[id];
      }
    });
  }, [currentPositions]);

  // Animation loop using setInterval
  useEffect(() => {
    const intervalId = setInterval(() => {
      const state = stateRef.current;
      const now = performance.now();
      const newPositions = {};
      let hasChanges = false;
      
      // Process all friends
      Object.keys(state.previous).forEach(id => {
        const target = state.targets[id];
        
        if (target) {
          // Animating
          const elapsed = now - target.startTime;
          const progress = Math.min(1, elapsed / INTERPOLATION_DURATION_MS);
          const eased = easeOutCubic(progress);
          
          const newLat = lerp(target.startLat, target.lat, eased);
          const newLon = lerp(target.startLon, target.lon, eased);
          
          newPositions[id] = { lat: newLat, lon: newLon };
          
          if (progress >= 1) {
            // Animation complete
            state.previous[id] = { lat: newLat, lon: newLon };
            delete state.targets[id];
          }
          hasChanges = true;
        } else {
          // Static position
          newPositions[id] = state.previous[id];
        }
      });
      
      // Only update state if there are changes or first render
      setSmoothedPositions(current => {
        // Check if anything actually changed
        const currentKeys = Object.keys(current);
        const newKeys = Object.keys(newPositions);
        
        if (currentKeys.length !== newKeys.length) {
          return newPositions;
        }
        
        for (const key of newKeys) {
          if (!current[key] || 
              Math.abs(current[key].lat - newPositions[key].lat) > 0.000001 ||
              Math.abs(current[key].lon - newPositions[key].lon) > 0.000001) {
            return newPositions;
          }
        }
        
        return current; // No changes
      });
    }, ANIMATION_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, []);

  return smoothedPositions;
};

export default useMultipleMarkerSmoothing;
